import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { GuestAuthProvider } from "@/context/GuestAuthContext";
import { CookieConsent } from "@/components/CookieConsent";
import { GoogleTag } from "@/components/GoogleTag";
import { SiteLayout } from "@/components/SiteLayout";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Home from "@/pages/Home";
import Apartments from "@/pages/Apartments";
import ApartmentDetail from "@/pages/ApartmentDetail";
import NotFound from "@/pages/NotFound";

// Code-Splitting: Seiten, die die meisten Besucher NIE öffnen (komplette Admin-Oberfläche,
// Rechtstexte, Konto-Seiten) werden erst bei Bedarf als eigene JS-Datei nachgeladen, statt
// direkt beim allerersten Seitenaufruf mitzuladen. Das verkleinert das Haupt-Bundle deutlich
// und war die Hauptursache für "823 KiB nicht verwendetes JavaScript" bei PageSpeed.
const BookingSuccess = lazy(() => import("@/pages/BookingSuccess"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminIntegrations = lazy(() => import("@/pages/AdminIntegrations"));
const GuestAuth = lazy(() => import("@/pages/GuestAuth"));
const GuestProfile = lazy(() => import("@/pages/GuestProfile"));
const Impressum = lazy(() => import("@/pages/Impressum"));
const Datenschutz = lazy(() => import("@/pages/Datenschutz"));
const AGB = lazy(() => import("@/pages/AGB"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));

function RouteFallback() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #C05C3B", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <LanguageProvider>
      <AuthProvider>
        <GuestAuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/apartments" element={<Apartments />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/apartment/:id" element={<ApartmentDetail />} />
                <Route path="/buchung/erfolg" element={<BookingSuccess />} />
                <Route path="/anmelden" element={<GuestAuth />} />
                <Route path="/registrieren" element={<GuestAuth />} />
                <Route path="/mein-konto" element={<GuestProfile />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/integrations" element={<AdminIntegrations />} />
            </Routes>
            </Suspense>
            <CookieConsent />
            <GoogleTag />
            <WhatsAppButton />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </GuestAuthProvider>
      </AuthProvider>
    </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
